import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatEUR, formatDate } from "@/components/app/Shared";
import QuickInvoiceCapture from "@/components/app/QuickInvoiceCapture";
import { Camera, Receipt, ArrowUpRight, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function InvoiceTile({ hidden, restaurantId, refreshKey, variant = "flat" }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    const q = restaurantId ? `?restaurant_id=${restaurantId}` : "";
    api.get(`/invoices/summary${q}`).then((r) => setData(r.data)).catch(() => setData({ month_count: 0, to_review: 0, recent: [] }));
  }, [restaurantId]);
  useEffect(() => { if (!hidden) load(); }, [hidden, load, refreshKey]);
  if (hidden) return null;

  const bento = variant === "bento";
  const wrap = bento ? "rounded-3xl p-6 bg-[#FBF3E4]" : "card-flat p-6";
  return (
    <div className={wrap} data-testid="invoice-tile">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-${bento ? "2xl" : "md"} bg-[#1B2D5C]/10 text-[#1B2D5C] flex items-center justify-center`}><Receipt size={20} /></div>
          <div>
            <div className="overline">Facturen</div>
            <h3 className="text-xl font-bold tracking-tight">Deze maand</h3>
          </div>
        </div>
        <Link to="/facturen" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="invoice-tile-open">
          Alles <ArrowUpRight size={13} />
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className={`rounded-2xl p-3 ${bento ? "bg-white/70" : "bg-zinc-50 border border-zinc-100"}`} data-testid="invoice-tile-month">
          <div className="text-3xl font-bold font-mono text-[#1B2D5C]">{data?.month_count ?? "—"}</div>
          <div className="text-xs text-zinc-500 font-semibold">facturen deze maand</div>
        </div>
        <Link to="/facturen?tab=controle" className={`rounded-2xl p-3 block ${data?.to_review ? "bg-amber-50 border border-amber-200" : bento ? "bg-white/70" : "bg-zinc-50 border border-zinc-100"}`} data-testid="invoice-tile-review">
          <div className={`text-3xl font-bold font-mono inline-flex items-center gap-1.5 ${data?.to_review ? "text-amber-700" : "text-emerald-700"}`}>
            {data?.to_review ?? "—"} {data?.to_review ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
          </div>
          <div className="text-xs text-zinc-500 font-semibold">{data?.to_review ? "te controleren" : "geen afwijkingen"}</div>
        </Link>
      </div>

      <QuickInvoiceCapture onSaved={load} defaultRestaurantId={restaurantId}>
        {(openCamera) => (
          <button type="button" onClick={openCamera}
            className="w-full h-14 rounded-full bg-[#1B2D5C] hover:bg-[#15244a] text-white font-bold inline-flex items-center justify-center gap-2 text-base shadow-lg shadow-[#1B2D5C]/20 active:scale-[0.99] transition-transform"
            data-testid="invoice-tile-camera">
            <Camera size={20} /> Factuur fotograferen
          </button>
        )}
      </QuickInvoiceCapture>

      <div className="mt-4 divide-y divide-zinc-200/60" data-testid="invoice-tile-recent">
        {(data?.recent || []).length === 0 && <div className="text-sm text-zinc-500 py-2">Nog geen facturen.</div>}
        {(data?.recent || []).map((inv) => (
          <Link to="/facturen" key={inv.id} className="flex items-center justify-between py-2 text-sm hover:opacity-80" data-testid={`invoice-tile-recent-${inv.id}`}>
            <div className="min-w-0">
              <div className="font-semibold truncate">{inv.supplier || inv.description || "Factuur"}</div>
              <div className="text-xs text-zinc-500 truncate">{inv.restaurant_name || "—"} · {formatDate(inv.created_at)}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {inv.ai_risk && (
                <span className={`text-[10px] font-bold px-1.5 h-5 inline-flex items-center rounded-full ${inv.ai_risk === "hoog" ? "bg-red-100 text-red-700" : inv.ai_risk === "midden" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {inv.ai_risk}
                </span>
              )}
              {inv.amount ? <span className="font-mono font-semibold">{formatEUR(inv.amount)}</span> : null}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
